/**
 * Swatch detail offcanvas: populate fields, available-color variants,
 * prev/next navigation (collections in browse, all matches when filtered),
 * and detail-panel add-to-cart.
 */
import { renderSwatchCard } from './swatch-card-render';

const DETAIL_PARAM = 'detail';

// The gallery's Color link identifies a swatch by what Widen tagged rather than by id. These
// four params are read and removed here, and deliberately none of them is a filter key.
const SWATCH_LINE_PARAM = 'swatchLine';
const SWATCH_COLLECTION_PARAM = 'swatchCollection';
const COLOR_NAME_PARAM = 'colorName';
const COLOR_NUMBER_PARAM = 'colorNumber';

// The id excludes the loading skeleton's placeholder tiles, which share .swatch-card-col.
const COLOR_CARD_SELECTOR = '.swatch-card-col[data-swatch-id]';

// The only attributes the colour-identity resolver and the product-line fallback read.
const RECORD_ATTRIBUTES = {
  'data-swatch-id': 'id',
  'data-collection': 'collection',
  'data-color-name': 'colorName',
  'data-color-number': 'colorNumber',
  'data-product-line-code': 'productLineCode',
  'data-productline': 'productLineId',
};

/**
 * Presents a catalog record as something with getAttribute, so code written against cards can
 * search records unchanged. Carries the record so a caller that needs real data can get at it
 * without parsing attributes back out.
 */
const recordAsCard = record => ({
  getAttribute: name => (
    Object.prototype.hasOwnProperty.call(RECORD_ATTRIBUTES, name)
      ? record[RECORD_ATTRIBUTES[name]]
      : null
  ),
  record,
});

// Mirrors SwatchPageController.Normalize, plus case folding, since the gallery sends Widen's
// wording rather than the catalog's.
const normalizeForMatch = value => String(value || '').trim().replace(/\s+/g, ' ').toLowerCase();

const createSwatchDetail = (ctx) => {
  const {
    app,
    state,
    detailPanel,
    filteredCards,
    visualizeUrl,
  } = ctx;

  const detailEls = detailPanel ? {
    image: detailPanel.querySelector('.swatch-detail-image'),
    name: detailPanel.querySelector('.swatch-detail-name'),
    sku: detailPanel.querySelector('.swatch-detail-sku'),
    line: detailPanel.querySelector('.swatch-detail-line'),
    related: detailPanel.querySelector('.swatch-detail-related'),
    favoriteSlot: detailPanel.querySelector('.swatch-detail-favorite-slot'),
    addCart: detailPanel.querySelector('.swatch-detail-add-cart'),
    visualize: detailPanel.querySelector('.swatch-detail-visualize'),
    features: detailPanel.querySelector('.swatch-detail-features'),
    featuresWrapper: detailPanel.querySelector('.swatch-detail-features-wrapper'),
    prev: detailPanel.querySelector('.prev-swatch'),
    next: detailPanel.querySelector('.next-swatch'),
    // The panel itself is overflow: hidden; this is the element that actually scrolls.
    body: detailPanel.querySelector('.offcanvas-body'),
  } : null;

  // The panel is reused rather than rebuilt, so it keeps whatever scroll offset the last
  // swatch was left at. Without this, opening a second swatch on a phone drops the user
  // partway down the new content.
  const resetDetailScroll = () => {
    if (detailEls && detailEls.body) {
      detailEls.body.scrollTop = 0;
    }
  };

  /**
   * Product-line visualizer URL + OE.CLR={colorNumber}, matching the legacy swatch
   * deep-link pattern (Model + ProgramFamilyName already on the product URL).
   */
  const buildSwatchVisualizerUrl = (productUrl, colorNumber) => {
    const base = (productUrl || '').trim();
    if (!base || base === '#') {
      return '#';
    }
    const color = (colorNumber || '').trim();
    if (!color) {
      return base;
    }
    if (/[?&]OE\.CLR=/i.test(base)) {
      return base;
    }
    const separator = base.indexOf('?') >= 0 ? '&' : '?';
    return `${base}${separator}OE.CLR=${encodeURIComponent(color)}`;
  };

  const collectionKeyOf = (productLineId, category, collection) => ([
    String(productLineId || '').trim(),
    String(category || '').trim(),
    String(collection || '').trim(),
  ].join('\u001f'));

  const collectionKey = card => collectionKeyOf(
    card.getAttribute('data-productline'),
    card.getAttribute('data-category'),
    card.getAttribute('data-collection'),
  );

  const recordCollectionKey = record => collectionKeyOf(
    record.productLineId,
    record.category,
    record.collection,
  );

  const swatchId = card => String(card.getAttribute('data-swatch-id') || '');

  /**
   * A catalog record as a real card element, so everything downstream keeps working on cards.
   *
   * Prefers a card already in the document, which the cart and favorite handlers keep in step,
   * and builds a detached one from the record only when the swatch has no card at all. In browse
   * mode that is nearly every swatch: the filtered grid stays empty until a filter is applied and
   * the strips carry one preview per collection. The markup is the grid renderer's own, so every
   * attribute populateDetail, findVariantCards and the cart item builder read is present.
   */
  const cardForRecord = (record) => {
    const id = String(record.id);
    const existing = app.querySelector(`.swatch-card-col[data-swatch-id="${id}"]`);

    if (existing) {
      return existing;
    }

    const favorites = ctx.favorites || {};
    const asset = Object.prototype.hasOwnProperty.call(favorites, id) ? favorites[id] : null;

    const host = document.createElement('div');
    host.innerHTML = renderSwatchCard(record, asset);

    return host.firstElementChild;
  };

  // One entry per collection (product line + category + collection), first occurrence wins.
  const dedupeByCollection = (items, keyOf) => {
    const seen = {};
    const unique = [];

    items.forEach((item) => {
      const key = keyOf(item);
      if (!key || key === '\u001f\u001f' || seen[key]) {
        return;
      }
      seen[key] = true;
      unique.push(item);
    });

    return unique;
  };

  const isFilteredMode = () => (
    typeof ctx.hasActiveFilters === 'function'
    && ctx.hasActiveFilters(state.filters)
  );

  /**
   * Browse (no filters): one representative card per collection (product line +
   * category + collection) so next/prev move between collections.
   * Filtered: every matching card, in filtered-grid order, so next/prev walk
   * individual swatches (including same-collection colors).
   *
   * Browse reads the catalog rather than the cards: until a filter is applied the only cards on
   * the page are the browse strip previews, so `filteredCards` is empty.
   */
  const getDetailPrevNextCards = (currentCard) => {
    if (isFilteredMode() && typeof ctx.getMatchingFilteredCards === 'function') {
      return ctx.getMatchingFilteredCards();
    }

    const lineId = currentCard
      ? String(currentCard.getAttribute('data-productline') || '').trim()
      : '';

    if (ctx.catalog) {
      const records = currentCard
        ? ctx.catalog.records.filter(record => String(record.productLineId) === lineId)
        : ctx.catalog.records;

      return dedupeByCollection(records, recordCollectionKey).map(cardForRecord);
    }

    const pool = currentCard
      ? filteredCards.filter(card => (
        String(card.getAttribute('data-productline') || '').trim() === lineId
      ))
      : filteredCards.slice();

    return dedupeByCollection(pool, collectionKey);
  };

  /**
   * Locate a card in the prev/next list. Prefer swatch id; fall back to
   * collection key so browse-mode related-color picks still map to the same slot.
   */
  const findNavIndex = (list, card) => {
    if (!list || !card) {
      return -1;
    }
    const id = swatchId(card);
    if (id) {
      for (let i = 0; i < list.length; i += 1) {
        if (swatchId(list[i]) === id) {
          return i;
        }
      }
    }
    const key = collectionKey(card);
    if (!key || key === '\u001f\u001f') {
      return -1;
    }
    for (let i = 0; i < list.length; i += 1) {
      if (collectionKey(list[i]) === key) {
        return i;
      }
    }
    return -1;
  };

  const getDetailOffcanvas = () => {
    if (!detailPanel || !window.bootstrap || !window.bootstrap.Offcanvas) {
      return null;
    }
    return window.bootstrap.Offcanvas.getOrCreateInstance(detailPanel);
  };

  // Which swatch the panel is showing lives in the URL so the panel survives the full
  // reload that follows logging in from it, and so a swatch can be linked to directly.
  // shared-filter-bar.js preserves this key when it rewrites the filter params.
  const updateSearchParams = (mutate) => {
    const params = new URLSearchParams(window.location.search);
    mutate(params);

    const query = params.toString();
    const { pathname, hash } = window.location;
    const nextUrl = (query ? `${pathname}?${query}` : pathname) + hash;

    if (nextUrl !== pathname + window.location.search + hash) {
      window.history.replaceState(null, '', nextUrl);
    }
  };

  const writeDetailParam = (detailSwatchId) => {
    updateSearchParams((params) => {
      if (detailSwatchId) {
        params.set(DETAIL_PARAM, String(detailSwatchId));
      } else {
        params.delete(DETAIL_PARAM);
      }
    });
  };

  const updateDetailAddCartLabel = () => {
    if (!detailEls || !detailEls.addCart || state.detailVariantIndex < 0) {
      return;
    }
    const card = state.detailVariantCards[state.detailVariantIndex];
    if (!card) {
      return;
    }
    const id = card.getAttribute('data-swatch-id');
    const inCart = window.isAuthenticated
      && ctx.readCart().some(item => String(item.id) === String(id));
    detailEls.addCart.textContent = inCart ? 'Remove' : 'Add to Cart';
    detailEls.addCart.setAttribute('aria-pressed', inCart ? 'true' : 'false');
  };

  /**
   * Color variants for the same product line + category + collection
   * (e.g. Cellular + 1 1/4" + CrystalPleat).
   */
  const findVariantCards = (card) => {
    const productLine = String(card.getAttribute('data-productline') || '').trim();
    const category = (card.getAttribute('data-category') || '').trim();
    const collection = (card.getAttribute('data-collection') || '').trim();
    if (!productLine || !category || !collection) {
      return [card];
    }

    if (ctx.catalog) {
      // Every color in the collection, whether or not it has a card yet: Available Colors is a
      // property of the fabric, so it does not change with how much of the grid is rendered.
      const variants = ctx.catalog.records
        .filter(record => (
          String(record.productLineId) === productLine
          && String(record.category || '').trim() === category
          && String(record.collection || '').trim() === collection
        ))
        .map(cardForRecord);

      return variants.length > 0 ? variants : [card];
    }

    const pool = filteredCards.length > 0
      ? filteredCards
      : Array.prototype.slice.call(app.querySelectorAll('.swatch-card-col'));

    const seen = {};
    const variants = [];
    pool.forEach((other) => {
      const otherId = String(other.getAttribute('data-swatch-id'));
      if (!otherId || seen[otherId]) {
        return;
      }
      if (String(other.getAttribute('data-productline') || '').trim() !== productLine) {
        return;
      }
      if ((other.getAttribute('data-category') || '').trim() !== category) {
        return;
      }
      if ((other.getAttribute('data-collection') || '').trim() !== collection) {
        return;
      }
      seen[otherId] = true;
      variants.push(other);
    });

    return variants.length > 0 ? variants : [card];
  };

  const renderRelated = (card) => {
    if (!detailEls || !detailEls.related) {
      return;
    }

    const currentId = String(card.getAttribute('data-swatch-id'));
    const variants = findVariantCards(card);

    detailEls.related.replaceChildren();
    variants.forEach((variantCard) => {
      const variantId = String(variantCard.getAttribute('data-swatch-id'));
      const isActive = variantId === currentId;
      const colorName = variantCard.getAttribute('data-color-name') || 'Color option';

      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = `swatch-detail-related-btn border-0 p-0 bg-transparent rounded-pill overflow-hidden${isActive ? ' is-active' : ''}`;
      btn.setAttribute('data-swatch-id', variantId);
      btn.setAttribute('aria-label', colorName);
      btn.setAttribute('aria-current', isActive ? 'true' : 'false');
      btn.disabled = isActive;

      const img = document.createElement('img');
      img.src = variantCard.getAttribute('data-image-url') || '';
      img.alt = colorName;
      img.width = 56;
      img.height = 56;
      img.className = 'object-fit-cover';
      btn.appendChild(img);

      if (!isActive) {
        btn.addEventListener('click', () => {
          let index = 0;
          for (let i = 0; i < variants.length; i += 1) {
            if (String(variants[i].getAttribute('data-swatch-id')) === variantId) {
              index = i;
              break;
            }
          }
          state.detailVariantCards = variants;
          state.detailVariantIndex = index;
          // Keep prev/next aligned with the color now on screen (esp. filtered mode,
          // where the next card in the list may be this same collection's other color).
          const navIdx = findNavIndex(state.detailPrevNextCards, variantCard);
          if (navIdx >= 0) {
            state.detailPrevNextIndex = navIdx;
          }
          // eslint-disable-next-line no-use-before-define
          populateDetail(variantCard);
        });
      }

      detailEls.related.appendChild(btn);
    });
  };

  /**
   * StyleKeys are "{productLineId}::{categoryName}" (pipe-joined on the card).
   * Strip the line-id prefix and return unique category display names.
   */
  const getCategoryNamesFromStyleKeys = (styleKeysAttr) => {
    const names = [];
    const seen = {};

    String(styleKeysAttr || '').split('|').forEach((raw) => {
      const key = String(raw || '').trim();
      if (!key) {
        return;
      }

      const sep = key.indexOf('::');
      const name = (sep >= 0 ? key.slice(sep + 2) : key).trim();
      if (!name || seen[name]) {
        return;
      }

      seen[name] = true;
      names.push(name);
    });

    return names;
  };

  const populateDetail = (card) => {
    if (!detailEls || !card) {
      return;
    }

    // Every route into the panel funnels through here - opening a card, prev/next, and
    // picking an available colour - so this is the one place the deep link can be kept
    // in step with what is on screen.
    writeDetailParam(card.getAttribute('data-swatch-id'));

    // Covers prev/next and colour variants, where the panel is already open and so
    // show.bs.offcanvas will not fire again.
    resetDetailScroll();

    const imageUrl = card.getAttribute('data-image-url') || '';
    const colorName = card.getAttribute('data-color-name') || '';
    const colorNumber = card.getAttribute('data-color-number') || '';
    const collection = card.getAttribute('data-collection') || '';
    const productLineName = card.getAttribute('data-productline-name') || '';
    const categories = getCategoryNamesFromStyleKeys(card.getAttribute('data-stylekeys'));

    if (detailEls.image) {
      detailEls.image.src = imageUrl;
      detailEls.image.alt = colorName;
    }
    if (detailEls.name) {
      detailEls.name.textContent = colorName;
    }
    if (detailEls.sku) {
      detailEls.sku.textContent = [collection, colorNumber].filter(Boolean).join(' \u2022 ');
    }
    if (detailEls.line) {
      detailEls.line.textContent = productLineName;
    }
    if (detailEls.features) {
      // The design shows a single bullet listing every category comma-separated, e.g.
      // "Available in 1 1/4" Single Cell - Blackout, 1/2" Double Cell - Blackout".
      detailEls.features.innerHTML = '';

      if (categories.length > 0) {
        const item = document.createElement('li');
        item.className = 'mb-3';
        item.textContent = `Available in ${categories.join(', ')}`;
        detailEls.features.appendChild(item);
      }
    }
    if (detailEls.featuresWrapper) {
      // Nothing to list leaves a bare "Features" heading, which the empty span used to do.
      detailEls.featuresWrapper.classList.toggle('d-none', categories.length === 0);
    }

    if (detailEls.visualize) {
      const productUrl = (card.getAttribute('data-visualize-url') || '').trim() || visualizeUrl || '';
      detailEls.visualize.setAttribute('href', buildSwatchVisualizerUrl(productUrl, colorNumber));
    }

    if (detailEls.favoriteSlot) {
      const sourceSlot = card.querySelector('.swatch-favorite-slot');
      detailEls.favoriteSlot.innerHTML = sourceSlot ? sourceSlot.innerHTML : '';
    }

    updateDetailAddCartLabel();
    renderRelated(card);
  };

  const openDetailForCard = (card) => {
    if (!detailPanel || !card) {
      return;
    }

    const navCards = getDetailPrevNextCards(card);
    let navIndex = findNavIndex(navCards, card);
    if (navIndex < 0 && navCards.length > 0) {
      navIndex = 0;
    }
    state.detailPrevNextCards = navCards;
    state.detailPrevNextIndex = navCards.length > 0 ? navIndex : -1;

    const variants = findVariantCards(card);
    const cardId = String(card.getAttribute('data-swatch-id'));
    let index = 0;
    for (let i = 0; i < variants.length; i += 1) {
      if (String(variants[i].getAttribute('data-swatch-id')) === cardId) {
        index = i;
        break;
      }
    }

    state.detailVariantCards = variants;
    state.detailVariantIndex = index;
    populateDetail(variants[index]);

    const instance = getDetailOffcanvas();
    if (instance) {
      instance.show();
      return;
    }

    detailPanel.classList.add('show');
  };

  /**
   * Makes sure stepping to `index` has a card to step to.
   *
   * In filtered mode the nav list only holds cards that have been rendered, so running off the
   * end means "render more", not "wrap around" - the same thing scrolling would have done. Only
   * a backwards wrap needs the whole set, because it has to land on the genuine last swatch.
   *
   * Returns the nav list to use, refreshed when rendering added anything.
   */
  const extendNavList = (list, index, delta) => {
    const needsMore = delta > 0 ? index >= list.length : index < 0;

    if (!needsMore || !isFilteredMode() || typeof ctx.hasMoreToRender !== 'function') {
      return list;
    }

    if (!ctx.hasMoreToRender()) {
      return list;
    }

    const added = delta > 0 ? ctx.renderMoreCards() : ctx.renderAllRemaining();

    if (added <= 0) {
      return list;
    }

    const refreshed = getDetailPrevNextCards(null);
    state.detailPrevNextCards = refreshed;
    return refreshed;
  };

  const goToDetailNav = (delta) => {
    let list = state.detailPrevNextCards;
    if (!list || list.length === 0) {
      return;
    }

    // Prefer the color currently shown (may differ after a related-color pick).
    const current = state.detailVariantCards[state.detailVariantIndex]
      || list[state.detailPrevNextIndex]
      || null;
    let index = findNavIndex(list, current);
    if (index < 0) {
      index = state.detailPrevNextIndex;
    }
    if (index < 0) {
      return;
    }

    const currentId = current ? swatchId(current) : '';
    let nextIndex = index;
    let attempts = 0;
    do {
      nextIndex += delta;

      // Render before wrapping, so the end of the rendered cards is not mistaken for the end of
      // the results. Only wraps once there is genuinely nothing left to render.
      list = extendNavList(list, nextIndex, delta);

      if (nextIndex < 0) {
        nextIndex = list.length - 1;
      } else if (nextIndex >= list.length) {
        nextIndex = 0;
      }
      attempts += 1;
      // Skip if we'd land on the same swatch already on screen (e.g. filtered list
      // has A1 then A2, user opened A1 and manually selected A2 via related colors).
    } while (
      attempts < list.length
      && currentId
      && swatchId(list[nextIndex]) === currentId
    );

    openDetailForCard(list[nextIndex]);
  };

  const onDetailAddCart = () => {
    const card = state.detailVariantCards[state.detailVariantIndex];
    if (!card) {
      return;
    }
    // A card built from a record is not in the document, so its button carries no cart state and
    // could not be found again after the login reload. Those fall through to the id-based path
    // below, which reads the cart itself.
    const button = card.querySelector('.swatch-cart-btn');
    if (button && document.contains(button)) {
      ctx.onCartClick(button);
      return;
    }

    if (!window.isAuthenticated) {
      ctx.launchAuthModal(() => {
        const item = ctx.buildCartItemFromCard(card);
        const items = ctx.readCart();
        if (!items.some(i => String(i.id) === String(item.id))) {
          items.push(item);
          ctx.writeCart(items);
          ctx.dispatchAddSwatchEvent(item);
        }
        window.location.reload();
      });
      return;
    }

    const id = card.getAttribute('data-swatch-id');
    const inCart = ctx.readCart().some(item => String(item.id) === String(id));
    if (inCart) {
      ctx.removeFromCart(id);
    } else {
      const item = ctx.buildCartItemFromCard(card);
      const items = ctx.readCart();
      items.push(item);
      ctx.writeCart(items);
      ctx.dispatchAddSwatchEvent(item);
      ctx.syncCartButtonsForId(id, true);
    }
  };

  const bindDetailEvents = () => {
    if (!detailPanel) {
      return;
    }

    detailPanel.addEventListener('submit', (event) => {
      if (event.target.closest('.swatch-favorite-form')) {
        ctx.onFavoriteSubmit(event);
      }
    });

    // populateDetail runs before the panel is shown, while it is still off-screen, so some
    // browsers discard the reset. Doing it again as the panel is revealed makes it stick,
    // and also catches anything that shows the panel without going through populateDetail.
    detailPanel.addEventListener('show.bs.offcanvas', () => {
      resetDetailScroll();
    });

    detailPanel.addEventListener('hidden.bs.offcanvas', () => {
      state.detailVariantIndex = -1;
      state.detailVariantCards = [];
      state.detailPrevNextIndex = -1;
      state.detailPrevNextCards = [];
      writeDetailParam(null);
    });

    if (detailEls && detailEls.addCart) {
      detailEls.addCart.addEventListener('click', onDetailAddCart);
    }

    if (detailEls && detailEls.prev) {
      detailEls.prev.addEventListener('click', (event) => {
        event.preventDefault();
        goToDetailNav(-1);
      });
    }

    if (detailEls && detailEls.next) {
      detailEls.next.addEventListener('click', (event) => {
        event.preventDefault();
        goToDetailNav(1);
      });
    }
  };

  // A swatch renders twice when it is in both a browse strip and the filtered grid, so the
  // uniqueness guards below have to count fabrics rather than elements.
  const distinctBySwatchId = (cards) => {
    const seen = {};

    return cards.filter((card) => {
      const id = String(card.getAttribute('data-swatch-id'));
      if (seen[id]) {
        return false;
      }
      seen[id] = true;
      return true;
    });
  };

  /**
   * Resolves the gallery's Color link to a card, since the gallery has no swatch id.
   */
  const findCardByColorIdentity = (collectionName, colorName, colorNumber, productLineCode) => {
    const wantedColor = normalizeForMatch(colorName);
    const wantedNumber = normalizeForMatch(colorNumber);

    if (!wantedColor && !wantedNumber) {
      return null;
    }

    const wantedCollection = normalizeForMatch(collectionName);
    const wantedLine = normalizeForMatch(productLineCode);

    // Searches the catalog rather than the DOM, because the filtered grid is now rendered on
    // demand: the swatch a gallery link points at usually has no card yet. Records are wrapped in
    // a getAttribute shim rather than rewriting the tiers below to read record fields - the tier
    // order and its uniqueness guards are load bearing (see TECH-DEBT section 13, three passes to
    // get them right) and this way they are not retyped at all.
    const cards = ctx.catalog
      ? ctx.catalog.records.map(recordAsCard)
      : Array.prototype.slice.call(app.querySelectorAll(COLOR_CARD_SELECTOR));

    const line = 'data-product-line-code';
    const coll = 'data-collection';
    const nameAttr = 'data-color-name';
    const numAttr = 'data-color-number';

    // `requireLine` narrows further rather than replacing `scope`, and is skipped when Widen
    // tagged no line at all.
    const matching = tier => cards.filter((card) => {
      if (tier.scope
        && normalizeForMatch(card.getAttribute(tier.scope)) !== tier.scoped) {
        return false;
      }
      if (tier.requireLine && wantedLine
        && normalizeForMatch(card.getAttribute(line)) !== wantedLine) {
        return false;
      }
      return normalizeForMatch(card.getAttribute(tier.attribute)) === tier.wanted;
    });

    const tiers = [];

    // Narrowest first. `unique` marks the tiers that must not guess between collections.
    if (wantedLine && wantedNumber) {
      tiers.push({ attribute: numAttr, wanted: wantedNumber, scope: line, scoped: wantedLine });
    }
    if (wantedLine && wantedColor) {
      tiers.push({
        attribute: nameAttr, wanted: wantedColor, scope: line, scoped: wantedLine, unique: true,
      });
    }
    // Collection and colour name are both reused across product lines, so the line is required.
    if (wantedCollection && wantedColor) {
      tiers.push({
        attribute: nameAttr, wanted: wantedColor, scope: coll, scoped: wantedCollection, requireLine: true,
      });
    }
    if (wantedCollection && wantedNumber) {
      tiers.push({
        attribute: numAttr, wanted: wantedNumber, scope: coll, scoped: wantedCollection, requireLine: true,
      });
    }
    // Unscoped tiers, which in practice fire for colours Widen tagged no line for. Still
    // line-required, because a tier that rejects a colour falls through to here rather than
    // failing it.
    if (wantedNumber) {
      tiers.push({
        attribute: numAttr, wanted: wantedNumber, unique: true, requireLine: true,
      });
    }
    if (wantedColor) {
      tiers.push({
        attribute: nameAttr, wanted: wantedColor, unique: true, requireLine: true,
      });
    }

    for (let i = 0; i < tiers.length; i += 1) {
      const tier = tiers[i];
      const found = matching(tier);
      const hits = distinctBySwatchId(found);

      if (hits.length > 0) {
        if (!tier.unique) {
          return hits[0];
        }

        // Blank collections are dropped rather than counted as one shared value.
        const collections = hits
          .map(card => normalizeForMatch(card.getAttribute(coll)))
          .filter(value => value.length > 0)
          .filter((value, index, list) => list.indexOf(value) === index);

        // The line carries the uniqueness test; the collection only has to not contradict it.
        const lines = hits
          .map(card => normalizeForMatch(card.getAttribute(line)))
          .filter((value, index, list) => list.indexOf(value) === index);

        if (collections.length <= 1 && lines.length === 1) {
          return hits[0];
        }
      }
    }

    return null;
  };

  /**
   * Stable entry point for the gallery link fixture in tools/swatch-link-fixture. Takes the four
   * url params verbatim and returns the swatch id they land on, or null.
   *
   * Returns the id rather than the element on purpose: the Design Gallery depends on this
   * resolution and a wrong answer is indistinguishable from a right one on screen, so the fixture
   * has to be able to diff before against after. An id is the one thing that stays comparable if
   * the resolver ever stops searching the DOM.
   */
  const resolveColorIdentity = ({
    swatchLine, swatchCollection, colorName, colorNumber,
  }) => {
    const card = findCardByColorIdentity(swatchCollection, colorName, colorNumber, swatchLine);
    return card ? card.getAttribute('data-swatch-id') : null;
  };

  /**
   * Narrows the grid to the resolved card's collection, so the panel opens over the filtered
   * view rather than the hidden browse page. The value is read off the card rather than the url
   * so it matches the filter option exactly, where Widen's wording may not. Falls back to the
   * product line for cards that carry no collection.
   */
  const applyCollectionFilterFromCard = (card) => {
    if (!ctx.filterBar) {
      return;
    }

    const current = ctx.filterBar.getState();

    // A collection already in the url wins; the configured Swatches url may carry its own.
    if ((current.collection || []).length > 0) {
      return;
    }

    const collectionValue = (card.getAttribute('data-collection') || '').trim();

    if (collectionValue) {
      ctx.filterBar.setState(Object.assign({}, current, { collection: [collectionValue] }));
      return;
    }

    // data-productline is the product line id, which is the `product` option value exactly.
    const productValue = (card.getAttribute('data-productline') || '').trim();

    if (!productValue || (current.product || []).length > 0) {
      return;
    }

    ctx.filterBar.setState(Object.assign({}, current, { product: [productValue] }));
  };

  /**
   * Last resort when no card resolves: narrow to the product line the photo named. The `product`
   * filter takes line ids rather than the code the gallery sends, and one code can cover several
   * lines, so the cards are used as the mapping.
   */
  const applyProductLineFallback = (productLineCode) => {
    const wantedLine = normalizeForMatch(productLineCode);

    if (!wantedLine || !ctx.filterBar) {
      return false;
    }

    const cards = ctx.catalog
      ? ctx.catalog.records.map(recordAsCard)
      : Array.prototype.slice.call(app.querySelectorAll(COLOR_CARD_SELECTOR));
    const lineIds = cards
      .filter(card => normalizeForMatch(card.getAttribute('data-product-line-code')) === wantedLine)
      .map(card => (card.getAttribute('data-productline') || '').trim())
      .filter((value, index, list) => value && list.indexOf(value) === index);

    if (lineIds.length === 0) {
      return false;
    }

    const current = ctx.filterBar.getState();

    if ((current.product || []).length > 0) {
      return false;
    }

    ctx.filterBar.setState(Object.assign({}, current, { product: lineIds }));
    return true;
  };

  /**
   * Reopens the panel from `?detail=<swatchId>` on load, or resolves the gallery's Color link to
   * a card when no swatch id is present. Returns the card it opened so the caller can bring it
   * into view, or null when no param is present or nothing on the page matches.
   */
  const openDetailFromUrl = () => {
    const params = new URLSearchParams(window.location.search);
    const detailSwatchId = params.get(DETAIL_PARAM);
    const galleryLine = params.get(SWATCH_LINE_PARAM);
    const galleryCollection = params.get(SWATCH_COLLECTION_PARAM);
    const colorName = params.get(COLOR_NAME_PARAM);
    const colorNumber = params.get(COLOR_NUMBER_PARAM);
    const isGalleryLink = !!(colorName || colorNumber);

    if (!detailSwatchId && !isGalleryLink) {
      return null;
    }

    // Identity resolution happens against the catalog, so `resolved` may be a record shim with no
    // element behind it yet. The real card is fetched further down, once the filter it implies has
    // been applied and the grid has rendered.
    const detailRecord = detailSwatchId && ctx.catalog
      ? ctx.catalog.get(detailSwatchId)
      : null;

    let resolved;
    if (detailRecord) {
      resolved = recordAsCard(detailRecord);
    } else if (detailSwatchId) {
      resolved = app.querySelector(`.swatch-card-col[data-swatch-id="${detailSwatchId}"]`);
    } else {
      resolved = findCardByColorIdentity(galleryCollection, colorName, colorNumber, galleryLine);
    }

    if (isGalleryLink || galleryCollection || galleryLine) {
      updateSearchParams((searchParams) => {
        searchParams.delete(SWATCH_LINE_PARAM);
        searchParams.delete(SWATCH_COLLECTION_PARAM);
        searchParams.delete(COLOR_NAME_PARAM);
        searchParams.delete(COLOR_NUMBER_PARAM);
      });
    }

    if (!resolved) {
      if (isGalleryLink && window.console && window.console.warn) {
        window.console.warn(
          'Swatches: no card for the gallery Color link',
          {
            swatchLine: galleryLine,
            swatchCollection: galleryCollection,
            colorName,
            colorNumber,
          },
        );
      }

      // Stale, hand-edited or discontinued; drop the param rather than leaving it to be re-shared.
      writeDetailParam(null);

      // Land somewhere related rather than on the browse page. Product line first, since it is
      // the half of the link the catalog is most likely to agree with.
      if (!applyProductLineFallback(galleryLine) && galleryCollection && ctx.filterBar) {
        const optionValue = ctx.filterBar.resolveOptionValue('collection', galleryCollection);
        const current = ctx.filterBar.getState();

        if (optionValue && (current.collection || []).length === 0) {
          ctx.filterBar.setState(Object.assign({}, current, { collection: [optionValue] }));
        }
      }

      return null;
    }

    // Gallery links only. A `?detail=` link already carries the visitor's own filters, and
    // narrowing further would change the grid height the caller's scroll restore reads.
    if (isGalleryLink) {
      applyCollectionFilterFromCard(resolved);
    }

    // Only now is there a grid to look in. ensureCardRendered forces chunks until this swatch
    // exists, so a deep link to something far down a large result set still opens rather than
    // silently landing on the browse page.
    const resolvedId = resolved.getAttribute('data-swatch-id');
    const card = (typeof ctx.ensureCardRendered === 'function'
      ? ctx.ensureCardRendered(resolvedId)
      : null)
      || app.querySelector(`.swatch-card-col[data-swatch-id="${resolvedId}"]`)
      // Browse mode has no grid to render into, so a `?detail=` link to anything other than a
      // strip preview has no card to find. Built from the record rather than dropped - this is
      // also the path a login from the panel comes back through.
      || (resolved.record ? cardForRecord(resolved.record) : null);

    if (!card) {
      writeDetailParam(null);
      return null;
    }

    // bootstrap.bundle.min.js is loaded async from <head> while this runs at the end of
    // <body>, so on a cold cache it may not have executed yet. Opening now would fall
    // through to toggling .show, which leaves the panel with no backdrop and no instance
    // for the close button to find. A click is always late enough; this is not.
    if (!window.bootstrap || !window.bootstrap.Offcanvas) {
      window.addEventListener('load', () => {
        openDetailForCard(card);
      }, { once: true });
      return card;
    }

    openDetailForCard(card);
    return card;
  };

  return {
    updateDetailAddCartLabel,
    openDetailForCard,
    openDetailFromUrl,
    resolveColorIdentity,
    bindDetailEvents,
  };
};

export default createSwatchDetail;
export { createSwatchDetail };
